import { Injectable, inject } from '@angular/core';
import { Tile } from 'src/app/components/tile/tile.component';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { HomescreenSetting, HomescreenSettings } from 'src/app/ngrx/settings/settings.models';
import { AuthService } from 'src/app/oidc/auth.service';

@Injectable({
  providedIn: 'root'
})
export class HomescreenService {
  private authService = inject(AuthService);


  public getAvailableHomescreenSettings(): HomescreenSettings {
    const tiles: Tile[] = this.getAvailableTiles();
    const homescreenSettings: HomescreenSettings = {};
    tiles.forEach((tile: Tile, idx: number): void => {
      homescreenSettings[tile.id] = { position: idx };
    })
    return homescreenSettings;
  }

  public visibleTilesToHomescreenSettings(homescreen: HomescreenSettings, tiles: Tile[]): HomescreenSettings {
    return Object.fromEntries(
      Object.entries(homescreen).map(([key, val]) => {
        const index = tiles.findIndex(t => t.id === key);
        return [key, { ...val, position: index > -1 ? index : null }];
      })
    );
  }

  /**
   * Returns a list of `Tile`s that are filtered based on the users roles.
   *
   * @returns
   */
  public getAvailableTiles(): Tile[] {
    const roles: readonly string[] = this.authService.userRoles() || [];
    return this.sequenceTiles(this.filteredTiles(environment.tiles, roles));
  }

  private sequenceTiles(tiles: readonly Tile[]): Tile[] {
    const homescreenEntries: Tile[] = [];
    for (const tile of tiles) {
      homescreenEntries.push(tile);
    }
    return homescreenEntries;
  }

  private filteredTiles(tiles: readonly Tile[], roles: readonly string[]): Tile[] {
    return tiles.filter((tile) => {
      // let tile without access role pass
      if (!tile?.access_roles) {
        return true;
      }
      for (const role of roles) {
        if (tile?.access_roles?.includes(role)) {
          return true;
        }
      }
      return false;
    });
  }

    /**
     * TODO VERSION / BUILD ENTSPRECHEND RELEASE ANPASSEN
     * @deprecated can be removed when all used Apps are above Version 5.10.0 OR no entries in table Settings in backend
     * parser for version 5.9.1 (356) -> 5.10.0 (364)
     * */
  public parseHomescreen(data: string[]): HomescreenSettings {
    const tileIds: Set<string> = new Set<string>(data);
    for (const { id } of this.getAvailableTiles()) {
      tileIds.add(String(id));
    }
    return Array.from(tileIds).reduce<HomescreenSettings>(
      (acc: HomescreenSettings, tile: string, index: number): HomescreenSettings => {
        acc[tile] = { position: data.includes(tile) ? index : null };
        return acc;
      },
      {}
    );
  }

public getVisibleTiles(homeScreen: HomescreenSettings): Tile[] {
  const availableTiles: Tile[] = this.getAvailableTiles();

  const tileMap = new Map(availableTiles.map((tile: Tile): [string, Tile] => [String(tile.id), tile]));

  return Object.entries(homeScreen)
    .filter(([, setting]: [string, HomescreenSetting]): boolean => setting.position !== null)
    .sort(([, a]: [string, HomescreenSetting], [, b]: [string, HomescreenSetting]): number => (a.position ?? 0) - (b.position ?? 0))
    .map(([id]: [string, HomescreenSetting]): Tile | null => {
      const tile: Tile = tileMap.get(id)!;
      return tile ? { ...tile, titleKey: tile.titleKey ?? `${tile.id}.title` } : null;
    })
    .filter((tile: Tile | null): tile is Tile => tile !== null);
  }

  public getInvisibleTiles(homeScreen: HomescreenSettings, translate: TranslateService): Tile[] {
    const tileMap = new Map(this.getAvailableTiles().map((tile: Tile): [string, Tile] => [String(tile.id), tile]));

    return Object.entries(homeScreen)
      .filter(([, setting]: [string, HomescreenSetting]): boolean => setting.position === null)
      .map(([id]: [string, HomescreenSetting]): Tile | null => {
        const tile: Tile | undefined = tileMap.get(id);
        if (!tile) return null;
        return { ...tile, titleKey: tile.titleKey ?? `${tile.id}.title` };
      })
      .filter((tile: Tile | null): tile is Tile => tile !== null)
      .sort((a: Tile, b: Tile) =>
        translate.instant(a.titleKey ?? `${a.id}.title`).localeCompare(translate.instant(b.titleKey ?? `${b.id}.title`))
      );
  }

  public setTilePosition(homeScreen: HomescreenSettings, tileId: string, position: number | null): HomescreenSettings {
    const tile: HomescreenSetting = homeScreen[tileId];
    if (!tile) return homeScreen;

    const currentPos = tile.position;

    // move from position to null
    if (position === null) {
      if (currentPos === null) return homeScreen;

      return Object.fromEntries(
        Object.entries(homeScreen).map(([key, value]: [string, HomescreenSetting]): [string, HomescreenSetting] =>
          [key, { ...value, position: key === tileId ? null : value.position !== null && value.position > currentPos ? value.position - 1 : value.position }]));
    }

    // move from null to position
    if (currentPos === null) {
      return Object.fromEntries(
        Object.entries(homeScreen).map(([key, value]: [string, HomescreenSetting]): [string, HomescreenSetting] =>
          [key, { ...value, position: key === tileId ? position : value.position !== null && value.position >= position ? value.position + 1 : value.position }]));
    }

    // move from currentPos to position
    if (currentPos === position) return homeScreen;

    return Object.fromEntries(
      Object.entries(homeScreen).map(([key, value]: [string, HomescreenSetting]): [string, HomescreenSetting] => {
        if (key === tileId) return [key, { ...value, position }];
        if (value.position === null) return [key, value];
        // move backward
        if (currentPos < position) return [key, { ...value, position: value.position > currentPos && value.position <= position ? value.position - 1 : value.position }];
        // move forward
        return [key, { ...value, position: value.position >= position && value.position < currentPos ? value.position + 1 : value.position }];
      })
    );
  }

  public cleanHomescreen(homeScreen: HomescreenSettings): HomescreenSettings {
    const ids: string[] = this.getAvailableTiles().map((tile: Tile): string => String(tile.id));
    const idSet = new Set(ids);

    const entries: [string, HomescreenSetting][] = Object.entries(homeScreen)
    .filter(([key]: [string, HomescreenSetting]): boolean => idSet.has(key))
    .sort(([, a]: [string, HomescreenSetting], [, b]: [string, HomescreenSetting]): number => {
      if (a.position === null) return 1;
      if (b.position === null) return -1;
      return a.position - b.position;
    });

    let pos = 0;

    return Object.fromEntries([
      ...ids.filter((id: string): boolean => !homeScreen.hasOwnProperty(id)).map(id => [id, { position: pos++ }]),
      ...entries.map(([id, s]: [string, HomescreenSetting]): [string, HomescreenSetting] => [id, { ...s, position: s.position === null ? null : pos++ }])
    ]);
  }
}
